import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { guidFor } from '@ember/object/internals';
import { t } from 'ember-intl';

import type { FieldValue } from './form';

/** Starts at 48px, not 32px: the smallest tier is mobile and must clear 44px. */
export type CodeInputSize = 'sm' | 'md' | 'lg';

/** Precomputed so the template needs no arithmetic. */
interface CodeBox {
  index: number;
  position: number;
  digit: string;
  isFirst: boolean;
}

interface CodeInputSignature {
  Args: {
    /** Emitted as `data-test-hook`; see `Input` for why this is not the DOM id. */
    testHook?: string;
    /** Digits entered so far. Shorter than `@length` leaves later boxes empty. */
    value?: FieldValue;
    /** Fires on every change with the code so far — never with a partial hole in it. */
    onChange?: (code: string) => void;
    /** Fires once when the last box fills — typed, pasted or autofilled — so it is
     * safe to submit from. */
    onComplete?: (code: string) => void;
    /** Number of digits @default 6 */
    length?: number;
    /** @default "md" */
    size?: CodeInputSize;
    /** Group's accessible name; falls back to a generic localized label. */
    label?: string;
    /** Any error paints every box — a wrong code is wrong as a whole. */
    errors?: string[];
    /** Disables every box, e.g. during a lockout. */
    isDisabled?: boolean;
    autofocus?: boolean;
  };
  Element: HTMLFieldSetElement;
}

const DEFAULT_LENGTH = 6;

/**
 * A one-time-code field rendered as one box per digit.
 *
 * The first box carries `autocomplete="one-time-code"`, so iOS and Android offer
 * the code straight from the SMS and most users never type — which makes the
 * multi-character branch of {@link handleInput} the path that matters.
 */
export default class CodeInput extends Component<CodeInputSignature> {
  private errorId = `tio-code-input-${guidFor(this)}-error`;
  private boxElements = new Map<number, HTMLInputElement>();

  private get length() {
    return this.args.length ?? DEFAULT_LENGTH;
  }

  private get size() {
    return this.args.size ?? 'md';
  }

  private get hasErrors() {
    return Boolean(this.args.errors?.length);
  }

  private get boxClasses() {
    const classes = ['app-code-box', `app-code-box--${this.size}`];
    if (this.hasErrors) classes.push('app-code-box--has-error');

    return classes.join(' ');
  }

  private get rowClasses() {
    return `app-code-row app-code-row--${this.size}`;
  }

  /** Digits only, never longer than `@length`. */
  private get code() {
    return String(this.args.value ?? '')
      .replace(/\D/g, '')
      .slice(0, this.length);
  }

  private get boxes(): CodeBox[] {
    const entered = this.code.split('');

    return Array.from({ length: this.length }, (_, index) => ({
      index,
      position: index + 1,
      digit: entered[index] ?? '',
      isFirst: index === 0,
    }));
  }

  /** Fires `@onComplete` only on the transition into a full code, so the paste and
   * final-keystroke paths cannot both fire for one completion. */
  private commit = (next: string) => {
    const previous = this.code;
    const code = next.replace(/\D/g, '').slice(0, this.length);

    this.args.onChange?.(code);
    this.focusBox(Math.min(code.length, this.length - 1));

    if (code.length === this.length && previous.length < this.length) {
      this.args.onComplete?.(code);
    }
  };

  private focusBox = (index: number) => {
    const box = this.boxElements.get(index);
    box?.focus();
    box?.select();
  };

  /** One character advances a box; anything longer is placed by {@link absorb}. */
  handleInput = (index: number, event: Event) => {
    const input = event.target as HTMLInputElement;
    const typed = input.value.replace(/\D/g, '');

    if (typed.length === 0) {
      // Clearing a box drops everything after it too — a code with a hole is never meant.
      this.commit(this.code.slice(0, index));
      return;
    }

    this.commit(this.absorb(typed, index));
  };

  /**
   * A batch holding a whole code IS the code wherever it lands: the OS offers the
   * SMS to whichever box has focus, so prefixing it with typed digits corrupts it.
   * Anything shorter overwrites from the receiving box and leaves what follows it
   * alone, so a two-digit paste into a filled row replaces two digits, not the rest.
   */
  private absorb = (digits: string, index: number) => {
    if (digits.length >= this.length) return digits;

    return this.code.slice(0, index) + digits + this.code.slice(index + digits.length);
  };

  handleKeydown = (index: number, event: KeyboardEvent) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const digits = this.boxes.map((box) => box.digit);

      if (digits[index]) {
        digits[index] = '';
      } else if (index > 0) {
        digits[index - 1] = '';
        this.focusBox(index - 1);
      }

      this.args.onChange?.(digits.join(''));
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusBox(index - 1);
    }

    if (event.key === 'ArrowRight' && index < this.length - 1) {
      event.preventDefault();
      this.focusBox(index + 1);
    }
  };

  handlePaste = (index: number, event: ClipboardEvent) => {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted) return;

    event.preventDefault();
    this.commit(this.absorb(pasted.replace(/\D/g, ''), index));
  };

  handleFocus = (event: FocusEvent) => {
    (event.target as HTMLInputElement).select();
  };

  // Ember owns setup and teardown of the element registry, so cross-box focus never
  // has to query the DOM from an event handler.
  private registerBox = modifier((element: HTMLInputElement, [index]: [number]) => {
    this.boxElements.set(index, element);

    return () => this.boxElements.delete(index);
  });

  // A modifier, not the `autofocus` attribute, which `no-autofocus-attribute` forbids.
  private focusOnInsert = modifier((element: HTMLElement, [enabled]: [boolean | undefined]) => {
    if (enabled) element.focus();
  });

  <template>
    <fieldset
      data-test-code-input
      data-test-hook={{@testHook}}
      class="tio-col-tight {{if @isDisabled 'opacity-50'}}"
      disabled={{@isDisabled}}
      aria-describedby={{this.errorId}}
      ...attributes
    >
      <legend class="sr-only">
        {{#if @label}}{{@label}}{{else}}{{t "common.code_input.group_label"}}{{/if}}
      </legend>

      <div class={{this.rowClasses}}>
        {{#each this.boxes key="index" as |box|}}
          <input
            data-test-code-input-box
            data-test-index={{box.index}}
            class="{{this.boxClasses}} {{if box.digit 'app-code-box--filled'}}"
            type="text"
            inputmode="numeric"
            autocomplete={{if box.isFirst "one-time-code" "off"}}
            {{this.registerBox box.index}}
            {{this.focusOnInsert (if box.isFirst @autofocus false)}}
            {{! No maxlength: it truncates the OS autofill to one digit. }}
            value={{box.digit}}
            disabled={{@isDisabled}}
            aria-label={{t "common.code_input.digit_label" position=box.position total=this.length}}
            aria-invalid={{if this.hasErrors "true" "false"}}
            {{on "input" (fn this.handleInput box.index)}}
            {{on "keydown" (fn this.handleKeydown box.index)}}
            {{on "paste" (fn this.handlePaste box.index)}}
            {{on "focus" this.handleFocus}}
          />
        {{/each}}
      </div>

      {{! Announced once for the group, not per digit. }}
      <div id={{this.errorId}} role="alert" class="tio-col-flush">
        {{#each @errors as |error|}}
          <p data-test-code-input-error class="text-xs tio-fg-danger">{{error}}</p>
        {{/each}}
      </div>
    </fieldset>
  </template>
}
